import { useEffect, useRef, useState, type ReactNode } from 'react';
import growhubLockup from '../assets/growhub-lockup.png';
import {
  COMPACT_MORE_GROUPS,
  COMPACT_NAV_SECTIONS,
  NAV_GROUPS,
  type SectionId,
} from './sections';
import styles from './AppShell.module.css';

type AppShellProps = {
  section: SectionId;
  onNavigate: (next: SectionId) => void;
  onSignOut: () => void | Promise<void>;
  /**
   * Sponsored inventory for the masthead. The slot reserves its height
   * whether or not it is filled (see --masthead-min), so a unit arriving
   * late never reflows the nav or the page heading underneath it.
   */
  promo?: ReactNode;
  /**
   * A live readout for the foot of the nav rail. The rail is taller than its
   * eight sections, and an empty 250px of chrome above the account group is
   * dead weight, so the space carries state instead.
   */
  railStatus?: ReactNode;
  children: ReactNode;
};

export function AppShell({
  section,
  onNavigate,
  onSignOut,
  promo,
  railStatus,
  children,
}: AppShellProps): ReactNode {
  const mainRef = useRef<HTMLElement>(null);
  const compactMenuButtonRef = useRef<HTMLButtonElement>(null);
  const previousSectionRef = useRef(section);
  const [isCompactMenuOpen, setIsCompactMenuOpen] = useState(false);

  // A route change replaces the page's primary content. Move focus with it so
  // keyboard and assistive-technology users do not remain in stale navigation.
  // Source: https://www.w3.org/WAI/WCAG22/Understanding/focus-order.html
  useEffect(() => {
    if (previousSectionRef.current === section) return;

    previousSectionRef.current = section;
    setIsCompactMenuOpen(false);
    mainRef.current?.focus();
  }, [section]);

  const compactMenuContainsCurrentSection = COMPACT_MORE_GROUPS.some((group) =>
    group.sections.some((entry) => entry.id === section),
  );

  const navigate = (next: SectionId): void => {
    setIsCompactMenuOpen(false);
    onNavigate(next);
  };

  return (
    <div className={styles.shell}>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      {/* The lockup sits across the top rather than inside the nav rail. At
          the rail's 13rem it rendered 151px wide, which put the "track. plan.
          grow." line at roughly four pixels tall and the sprout at the size
          of a smudge. Full width gives the artwork the ~180px it needs to
          read, and it puts the brand on the same horizontal band as the
          promo slot instead of stacked above the sections. */}
      <div className={styles.masthead}>
        <h1 className={styles.brand}>
          <img
            className={styles.brandLockup}
            src={growhubLockup}
            alt="GrowHub — track. plan. grow."
            width={531}
            height={128}
          />
        </h1>

        {promo ? <div className={styles.promo}>{promo}</div> : null}
      </div>

      <header className={styles.sidebar}>
        <nav className={styles.desktopNavigation} aria-label="Sections">
          <div className={styles.navGroups}>
            {NAV_GROUPS.map((group) => (
              <div
                className={styles.navGroup}
                data-nav-group={group.id}
                key={group.id}
              >
                <span className={styles.navGroupLabel}>{group.label}</span>
                <ul className={styles.navList} aria-label={group.label}>
                  {group.sections.map((entry) => (
                    <li key={entry.id}>
                      <button
                        type="button"
                        className={styles.navLink}
                        aria-current={entry.id === section ? 'page' : undefined}
                        onClick={() => {
                          navigate(entry.id);
                        }}
                      >
                        {entry.label}
                      </button>
                    </li>
                  ))}
                  {group.id === 'account' ? (
                    <li>
                      <button
                        type="button"
                        className={`${styles.navLink} ${styles.signOut}`}
                        onClick={() => {
                          void onSignOut();
                        }}
                      >
                        Sign out
                      </button>
                    </li>
                  ) : null}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <nav
          className={styles.compactNavigation}
          aria-label="Compact sections"
          onKeyDown={(event) => {
            if (event.key === 'Escape' && isCompactMenuOpen) {
              setIsCompactMenuOpen(false);
              compactMenuButtonRef.current?.focus();
            }
          }}
        >
          <ul className={styles.compactNavList}>
            {COMPACT_NAV_SECTIONS.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  className={styles.compactNavLink}
                  aria-current={entry.id === section ? 'page' : undefined}
                  onClick={() => {
                    navigate(entry.id);
                  }}
                >
                  {entry.id === 'knowledge' ? 'Learn' : entry.label}
                </button>
              </li>
            ))}
            <li>
              <button
                ref={compactMenuButtonRef}
                type="button"
                className={styles.compactNavLink}
                aria-expanded={isCompactMenuOpen}
                aria-controls="compact-navigation-more"
                aria-current={
                  compactMenuContainsCurrentSection ? 'page' : undefined
                }
                onClick={() => {
                  setIsCompactMenuOpen((current) => !current);
                }}
              >
                More
              </button>
            </li>
          </ul>

          {isCompactMenuOpen ? (
            <div
              id="compact-navigation-more"
              className={styles.compactMorePanel}
            >
              {COMPACT_MORE_GROUPS.map((group) => (
                <div className={styles.compactMoreGroup} key={group.id}>
                  <span className={styles.compactMoreLabel}>{group.label}</span>
                  <ul className={styles.compactMoreList} aria-label={group.label}>
                    {group.sections.map((entry) => (
                      <li key={entry.id}>
                        <button
                          type="button"
                          className={styles.compactMoreLink}
                          aria-current={
                            entry.id === section ? 'page' : undefined
                          }
                          onClick={() => {
                            navigate(entry.id);
                          }}
                        >
                          {entry.label}
                        </button>
                      </li>
                    ))}
                    {group.id === 'account' ? (
                      <li>
                        <button
                          type="button"
                          className={`${styles.compactMoreLink} ${styles.signOut}`}
                          onClick={() => {
                            setIsCompactMenuOpen(false);
                            void onSignOut();
                          }}
                        >
                          Sign out
                        </button>
                      </li>
                    ) : null}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </nav>

        {railStatus ? (
          <div className={styles.railStatus}>{railStatus}</div>
        ) : null}
      </header>

      <main
        ref={mainRef}
        id="main-content"
        className={styles.main}
        tabIndex={-1}
      >
        <div className={styles.content}>{children}</div>

        <p className={styles.status}>
          <span>
            Stored to your GrowHub account <span className={styles.statusDot}>·</span>{' '}
            scoped to you
          </span>
          <span>Export a backup from Settings &amp; Data to keep a local copy</span>
        </p>
      </main>
    </div>
  );
}
